import { useEffect, useState, useContext, useMemo } from 'react';
import API from '@aws-amplify/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import i18n from '../../../../i18n';
import { toast } from 'react-toastify';
import { t } from 'i18next';

import Button from '../../../shared/Button';
import { ProtectedRouteContext } from '../../../Context';
import FileDrop from '../../../shared/FileDrop';

import './File.scss';
import { Box, Dialog, DialogContent } from '@mui/material';

export default function File(props) {
    /**
     * @type {ShipyardQuestion}
     */
    const question = props.question;
    const shouldShowErrors = props.shouldShowErrors;
    const disabled = props.disabled;
    const appInstanceId = props.appInstanceId;
    const answerUpdated = props.answerUpdated;
    const previouslyChosen =
        !!question.docData && typeof question.docData.map === 'function'
            ? question.docData.map((doc) => {
                  return Object.assign(
                      {},
                      {
                          name: doc.FileName,
                          uploadedOn: doc.LastUpdatedUTC,
                          type: doc.FileName.includes('pdf') ? 'pdf' : doc.FileName.includes('.docx') ? 'officedocument' : 'default',
                          fileId: doc.FileID,
                      }
                  );
              })
            : [];

    const { userSettings, displayModal } = useContext(ProtectedRouteContext);
    const [lang, setLang] = useState(
        typeof userSettings?.LanguagePreference === 'string' ? userSettings.LanguagePreference : i18n.language
    );
    const [saving, setSaving] = useState(false);
    const [files, setFiles] = useState(previouslyChosen);
    const [del, setDel] = useState(false);
    // const [min] = useState(!!question.minResponseRequired && typeof question.minResponseRequired === "number" ? question.minResponseRequired : 0);
    const [max] = useState(
        !!question.maxResponseRequired && typeof question.maxResponseRequired === 'number' ? question.maxResponseRequired : 0
    );

    useEffect(() => {
        if (typeof userSettings?.LanguagePreference === 'string') {
            setLang(userSettings.LanguagePreference);
        }
    }, [userSettings.LanguagePreference]);

    useEffect(() => {
        if (Number.isInteger(del)) {
            let deleteFile = async () => {
                setSaving(true);

                try {
                    await API.del('ShipyardAPI', `/application/docs/${del}`, {
                        body: {
                            status: '0',
                        },
                    });

                    setDel();

                    question.chosenOptions = files.filter((file) => file.fileId !== del).map((file) => file.fileId);
                    question.docData = files
                        .filter((file) => file.fileId !== del)
                        .map((file) => {
                            return {
                                FileName: file.name,
                                FileID: file.fileId,
                                LastUpdatedUTC: file.uploadedOn,
                            };
                        });
                    var validity = question.validator(question);
                    question.isValid = validity.isValid;
                    question.invalidReason = validity.invalidReason;
                    answerUpdated();

                    setFiles((prev) => prev.filter((file) => file.fileId !== del));
                } catch (e) {
                    toast.error(t('General.AjaxError'));

                    setDel();
                }

                setSaving(false);
            };
            deleteFile();
        }
    }, [del, files, answerUpdated, question]);

    const fileJSX = useMemo(() => {
        const handleDrop = async (event) => {
            event?.preventDefault();
            event?.stopPropagation();

            let _fileDropped = event?.dataTransfer?.files || event?.target?.files;

            let _validFiles = question.responseRegexes.map((obj) => new RegExp(obj.rgx));

            if (_fileDropped && _validFiles.every((rgx) => rgx.test(_fileDropped[0].type))) {
                setSaving(true);

                //API call to create file metadata within the database and receive an S3 url
                let s3Call;
                try {
                    s3Call = await API.get('ShipyardAPI', `/application/${appInstanceId}/docs/${question.id}/target`, {
                        queryStringParameters: {
                            fileName: _fileDropped[0].name,
                            documentType: question.documentType,
                            mimeType: _fileDropped[0].type,
                        },
                    });
                } catch (e) {
                    toast.error(t('General.AjaxError'));

                    setSaving(false);

                    return;
                }

                //fetch() call here to the URL provided by the above API call
                let fetchCall;
                try {
                    let options = {
                        body: _fileDropped[0],
                        headers: {
                            'Content-Type': '',
                        },
                        method: 'PUT',
                    };

                    fetchCall = await fetch(s3Call.url, options);

                    if (!fetchCall.ok) {
                        toast.error(t('General.AjaxError'));

                        setSaving(false);

                        return;
                    }
                } catch (e) {
                    toast.error(t('General.AjaxError'));

                    setSaving(false);

                    return;
                }

                try {
                    await API.put('ShipyardAPI', `/application/docs/${s3Call.fileId}`, {
                        body: {
                            status: '1', //Uploaded
                        },
                    });
                } catch (e) {
                    toast.error(t('General.AjaxError'));

                    setSaving(false);

                    return;
                }

                let _file = {
                    name: _fileDropped[0].name,
                    fileId: s3Call.fileId,
                    uploadedOn: new Date().toISOString(),
                    type: _fileDropped[0].type,
                };

                //Update the pertinent question answer with the relative file metadata
                let _files = files.map((file) => file.fileId);
                try {
                    var newChosenVals = [..._files, s3Call.fileId];
                    await API.put('ShipyardAPI', `/application/${appInstanceId}/answer/${question.id}`, {
                        body: { responseIds: newChosenVals },
                    });
                } catch (e) {
                    toast.error(t('General.AjaxError'));

                    setSaving(false);

                    return;
                }

                _files = files.map((file) => {
                    return {
                        FileName: file.name,
                        FileID: file.fileId,
                        LastUpdatedUTC: file.uploadedOn,
                    };
                });

                question.chosenOptions = newChosenVals;
                question.docData = [
                    ..._files,
                    {
                        FileName: _fileDropped[0].name,
                        FileID: s3Call.fileId,
                        LastUpdatedUTC: new Date().toISOString(),
                    },
                ];
                var validity = question.validator(question);
                question.isValid = validity.isValid;
                question.invalidReason = validity.invalidReason;
                answerUpdated();

                setFiles((prev) => [...prev, _file]);

                setSaving(false);
            } else {
                toast.error(t('File.IncorrectType'));

                setSaving(false);
            }
        };

        const handleDrag = (event) => {
            event?.preventDefault();
            event?.stopPropagation();
        };

        const handleRemove = (id) => {
            let param2 = () => (
                <Dialog open={true}>
                    <DialogContent className="deletion-warning-container">
                        <Box className="deletion-warning-text">
                            <label>Are you sure you want to delete:</label>
                            <br />
                            {files.find((file) => file.fileId === id).name}
                        </Box>
                        <Box>
                            <Button
                                useMui={true}
                                type="negative"
                                onClick={() => {
                                    displayModal(false, null);
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                useMui={true}
                                onClick={() => {
                                    displayModal(false, null);
                                    setDel(id);
                                }}
                            >
                                Continue
                            </Button>
                        </Box>
                    </DialogContent>
                </Dialog>
            );
            displayModal(true, param2);
        };

        let _fileListJSX = [];

        files.forEach((file, filei) => {
            _fileListJSX.push(
                <FileDrop
                    key={`file-drop-${filei}`}
                    drag={handleDrag}
                    drop={handleDrop}
                    remove={handleRemove}
                    files={[file]}
                    required={question.minResponseRequired > 0}
                />
            );
        });

        if (files.length < max)
            _fileListJSX.push(
                <FileDrop
                    key={`file-drop-${files.length}`}
                    drag={handleDrag}
                    drop={handleDrop}
                    remove={handleRemove}
                    files={[]}
                    required={question.minResponseRequired > 0}
                />
            );

        return (
            <div
                className="attachments-files-list"
                onDragOver={handleDrag}
                onDrop={handleDrag}
            >
                {_fileListJSX}
            </div>
        );
    }, [files, question, max, answerUpdated, appInstanceId, displayModal]);

    const fileTypeJSX = useMemo(() => {
        if (files.length < 1) {
            return <span>{t('File.AcceptedTypes', { acceptedFileTypes: question.acceptedFileTypes })}</span>;
        } else {
            return null;
        }
    }, [files, question.acceptedFileTypes]);

    return (
        <div
            className={'question-wrapper file' + (!question.isValid && shouldShowErrors ? ' invalid' : '') + (disabled ? ' disabled' : '')}
        >
            <div className="label label-file">
                <span>{question.text[lang]}</span>
                <div className="line"></div>
                {saving ? (
                    <span className="saving">
                        <FontAwesomeIcon icon={faSpinner} />
                    </span>
                ) : null}
            </div>
            <span className="invalid-reason">
                {question.isValid
                    ? ''
                    : typeof question.invalidReason === 'string'
                    ? t(question.invalidReason, { max: question.maxResponseRequired, min: question.minResponseRequired })
                    : ''}
            </span>
            {fileJSX}
            {fileTypeJSX}
        </div>
    );
}
